import {
  RequestErrorUnauthenticated,
  RequestErrorBadData,
  RequestErrorRedirected,
} from "@errors/RequestError";
import { whitelistKeys } from "@helper/object";
import { getFullName } from "@domain/user";
import { makeLocaleSort } from "@helper/array";
import { splitFilename } from "@services/files";
import appConfig from "@/appConfig";
import { persistRole } from "@dux/session";
import qs from "qs";

export function getSessionError({
  result,
  sitemap,
  sessionModelUser,
  roles,
  localization,
  url,
}) {
  if (result === "AuthenticationRequired") {
    return RequestErrorUnauthenticated();
  }
  if (result === "Redirect") {
    return RequestErrorRedirected({ url });
  }
  if (!sitemap || !localization || !sessionModelUser || !roles) {
    return RequestErrorBadData();
  }
  return null;
}

const draftTypes = [
  { id: "locales", folder: "locales", parse: (f) => splitFilename(f, -1) },
  { id: "sitemaps", folder: "sitemaps", parse: (f) => splitFilename(f, -1) },
  { id: "processes", folder: "processes", parse: (f) => splitFilename(f, 1) },
  { id: "layouts", folder: "files", parse: (f) => splitFilename(f, 2) },
  { id: "encodings", folder: "files", parse: (f) => splitFilename(f, 2) },
  { id: "badgetemplates", folder: "files", parse: (f) => splitFilename(f, 2) },
  { id: "templates", folder: "files", parse: (f) => splitFilename(f, 1) },
  { id: "files", folder: "files", parse: (f) => splitFilename(f, 1) },
  { id: "pdfs", folder: "files", parse: (f) => splitFilename(f, 1) },
  { id: "documents", folder: "files", parse: (f) => splitFilename(f, 1) },
  { id: "images", folder: "images", parse: (f) => splitFilename(f, 1) },
  { id: "objects", folder: "objects", parse: (f) => splitFilename(f, 1) },
  {
    id: "stylesheets",
    folder: "stylesheets",
    parse: (f) => splitFilename(f, 1),
  },
  { id: "titles", folder: "titles", parse: (f) => splitFilename(f, 1) },
  {
    id: "enumerations",
    folder: "enumerations",
    parse: (f) => splitFilename(f, 1),
  },
];
export function buildCopyUserDraftParams({
  deploymentId,
  commitMessage,
  userId,
  draft,
}) {
  if (!draft) return null;

  let ret = {
    deploymentId,
    commitMessage,
    userId,
    push: true,
  };

  draftTypes.map((t) => {
    ret[t.id] = draft[t.id]
      .filter((l) => l.copy)
      .map((l) => ({
        version: l.version,
        name: l.filename,
      }));
  });

  return ret;
}

export function buildDeployPendingDraftParams({
  deploymentId,
  commitMessage,
  draft,
  ...params
}) {
  let ret = {
    ...params,
    deploymentId,
    commitMessage,
    push: true,
  };

  draftTypes.map((t) => {
    ret[t.id] = draft[t.id]
      .filter((l) => l.deploy)
      .map((l) => ({
        version: l.version,
        name: l.name,
        filename: l.filename,
      }));
  });

  return ret;
}

export function buildDiscardPendingDraftParams({
  deploymentId,
  commitMessage,
  draft,
  ...params
}) {
  let ret = {};
  let hasUnselected = false;
  draftTypes.map((t) => {
    hasUnselected =
      hasUnselected || draft[t.id].filter((l) => !l.deploy).length > 0;
    ret[t.id] = draft[t.id]
      .filter((l) => l.deploy)
      .map((l) => ({
        version: l.version,
        name: l.name,
        filename: l.filename,
      }));
  });

  return hasUnselected ? ret : null;
}

function parseDraft(data) {
  var parsed = {
    hasChanges: false,
    user: {
      id: data.id,
      fullName: getFullName(data),
    },
  };

  draftTypes.map((t) => {
    parsed[t.id] = data[t.id]
      .map(whitelistKeys(["name", "version"]))
      .map((s) => {
        if (data[t.id].length > 0) parsed.hasChanges = true;
        return s;
      })
      .map((s) => ({
        ...s,
        folder: t.folder,
        ...t.parse(s.name),
        deploy: true,
        copy: true,
      }))
      .sort(makeLocaleSort((s) => s.folder + "/ " + s.name));
  });

  return parsed;
}

export function parsePendingDraft(data, activeUserId) {
  const drafts = data.users.map(parseDraft);

  const userDrafts = drafts.filter((d) => d.user.id !== activeUserId);
  const activeDraft = drafts.find((d) => d.user.id === activeUserId);

  // note: we do not set the pendingDraft if it doesn't have any changes
  // to not break the logic of the button to copy or publish
  return {
    deploymentId: data.deploymentId,
    pendingDraft: activeDraft && activeDraft.hasChanges ? activeDraft : null,
    userDrafts,
  };
}

export function redirectToLogin() {
  const queryRole =
    qs.parse(window.location.search, {
      ignoreQueryPrefix: true,
    })?.role || null;
  const returnUrl =
    process.env.NODE_ENV === "development"
      ? window.location.origin + window.location.pathname
      : window.location.pathname;
  var url = `${appConfig.loginUrl}?returnUrl=${encodeURIComponent(returnUrl)}`;
  if (queryRole && queryRole !== "") {
    persistRole(queryRole);
    url = `${url}&role=${queryRole}`;
  }
  window.location.replace(url);
}
